/* eslint-disable react/forbid-component-props */
/* eslint-disable no-restricted-imports */
import { Box, Grid, GridProps, IconButton, Typography } from '@mui/material';
import { FC, MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { ProductType } from '@/data/types/Product';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { Img } from '@/components/blocks/MaterialImage';
import {
	productPLPAddToCartButtonSX,
	productPLPContentSX,
	productPLPDiscountSX,
	productPLPFavoriteButtonSX,
	productPLPImageSX,
	productPLPPriceSX,
	productPLPSizeSX,
	productPLPSubtitleSX,
	productPLPTitleSX,
} from '@/components/content/CatalogEntryList/styles/typography';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { combineSX } from '@/utils/combineSX';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { Linkable } from '@/components/blocks/Linkable';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { mergeRibbonsForProduct } from '@/utils/mergeRibbonsForProduct';
import { ProductRibbons } from '@/components/blocks/Ribbons';
import { TYPES } from '@/data/constants/product';
import { generateBundleDefaultImageUrl } from '@/components/content/Bundle/parts/BundleGallery';
import { ProductCardBundlePriceContentRecommendation } from '@/components/content/CatalogEntryList/parts/ProductCardBundlePriceContentRecommendation';
import { usePriceToCheckDpOffer } from 'integration/utils/isDpOffer';
import { useProductExtractMerchandisingAssociations } from 'integration/hooks/useProductExtractMerchandisingAssociations';
import { ProductCardSelectColor } from '@/components/blocks/ProductCardSelectColor';
import { orderColorVariantsByBase } from '@/utils/productColor';

interface InfoElectronico {
	offerDeContado: string | undefined;
	strikedDeContado: string | undefined; // EL QUE SE DEBE DE TACHAR
}

export interface ProductCardPLPProps extends GridProps {
	product: ProductType;
	clickAction?: () => void;
	isFavorite?: boolean;
	infoElectronico?: InfoElectronico;
	localstorageRecord?: Record<string, unknown>;
	onToggleFavorite?: MouseEventHandler<HTMLButtonElement>;
	onAddToCart?: MouseEventHandler<HTMLButtonElement>;
}

export const ProductCardPLP: FC<ProductCardPLPProps> = ({
	product: _product,
	isFavorite = false,
	onToggleFavorite,
	infoElectronico,
	localstorageRecord,
	onAddToCart,
	clickAction,
	...rest
}) => {
	const [product, setProduct] = useState(_product);
	const { productVariantColors } = useProductExtractMerchandisingAssociations(_product);
	const orderedVariantColors = useMemo(
		() => orderColorVariantsByBase(productVariantColors, _product.partNumber),
		[productVariantColors, _product.partNumber]
	);
	const { onClick } = useProductEvents({ product });
	const [isClick, setIsClick] = useState(false);
	const ribbons = mergeRibbonsForProduct(product.ribbons);
	const size = findProductAttribute(product.attributes, 'Talla')?.value;
	const isBundle = product.type === TYPES.bundle;

	useEffect(() => {
		const timeout = setTimeout(() => {
			setIsClick(false);
		}, 1000);
		return () => clearTimeout(timeout);
	}, [isFavorite]);

	const handleFavoriteClick = (e: any) => {
		e.preventDefault();
		e.stopPropagation();
		setIsClick(true);
		onToggleFavorite?.(e);
	};

	const { discount, price } = usePriceToCheckDpOffer({ infoElectronico, product });

	const sxPrice = productPLPPriceSX(discount !== null, false);

	const priceContent = (
		<>
			{discount ? <Typography sx={productPLPDiscountSX(false)}>{discount}</Typography> : null}
			<Typography sx={sxPrice}>{price}</Typography>
		</>
	);

	const fullImageRaw = isBundle
		? generateBundleDefaultImageUrl(product.partNumber, 1)
		: product.thumbnail;

	return (
		<Grid
			className="product-card-plp-container"
			item
			xs={6}
			md={4}
			{...rest}
			id={product.partNumber}
		>
			<Box
				className="product-card-plp-content"
				border="1px solid #DADCE0"
				onClick={onClick(clickAction)}
			>
				<Linkable
					href={
						{
							pathname: `${product?.seo?.href}`,
							localstorage: localstorageRecord,
						} as any
					}
					className="product-card-plp-linkable"
				>
					<RenderProductImg
						fullImageRaw={fullImageRaw}
						render={(src) => (
							<Grid className="product-card-plp-image-frame" sx={{ position: 'relative' }}>
								<ProductRibbons ribbons={ribbons} />

								{onToggleFavorite ? (
									<IconButton
										disabled={isClick}
										sx={productPLPFavoriteButtonSX}
										onClick={handleFavoriteClick}
									>
										{isFavorite ? (
											<FavoriteIcon color="primary" />
										) : (
											<FavoriteBorderIcon color="primary" />
										)}
									</IconButton>
								) : null}
								<Img
									className="product-card-plp-image"
									src={src}
									sx={productPLPImageSX(isFavorite)}
									alt={product.name}
								/>
							</Grid>
						)}
					/>
					<Box sx={productPLPContentSX(isFavorite)}>
						{isBundle ? (
							<ProductCardBundlePriceContentRecommendation
								partNumber={product.partNumber}
								name={product.name}
								manufacturer={product.manufacturer ?? 'Combo'}
								discount={discount}
								price={price}
							/>
						) : isFavorite ? (
							<>
								<Box>
									<Typography sx={productPLPTitleSX(isFavorite)}>{product.name}</Typography>
									<Typography
										sx={combineSX([productPLPSubtitleSX, { fontWeight: '300', mb: '4px' }])}
									>
										{product.manufacturer}
									</Typography>
									{size ? <Typography sx={productPLPSizeSX}>{`Talla ${size}`}</Typography> : null}
									<Box display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
										{priceContent}
									</Box>
								</Box>
								<DpButton
									sx={combineSX([
										productPLPAddToCartButtonSX,
										isFavorite
											? {
													width: '100%',
													maxWidth: '400px !important',
													'@media (max-width: 375px)': {
														maxWidth: '140px !important',
														fontSize: '10px !important',
													},
													'@media (max-width: 768px)': {
														maxWidth: '140px !important',
													},
											  }
											: {},
									])}
									size="small"
									fullWidth
									disabled={!size}
									onClick={onAddToCart}
								>
									{'Agregar al carrito'}
								</DpButton>
							</>
						) : (
							<Box>
								<ProductCardSelectColor
									variants={orderedVariantColors}
									selectedPartNumber={product.partNumber}
									sx={{ mb: '8px', mt: '2px' }}
									onSelectColor={(variant) => setProduct(variant)}
								/>
								<Typography sx={productPLPTitleSX(isFavorite)}>{product.name}</Typography>
								<Typography sx={combineSX([productPLPSubtitleSX, { fontWeight: '300' }])}>
									{product.manufacturer}
								</Typography>
								<Box display={'flex'} flexDirection={'row'} flexWrap={'wrap'} sx={{ mt: '10px' }}>
									{priceContent}
								</Box>
							</Box>
						)}
					</Box>
				</Linkable>
			</Box>
		</Grid>
	);
};
